import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { listingsApi, categoriesApi, citiesApi, userApi, uploadApi, geoApi, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { Upload, X, Loader2, AlertTriangle, ImagePlus, Sparkles } from 'lucide-react';

const MAX_IMAGES = 5;

export default function CreateListingPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [images, setImages] = useState([]); // [{file, preview, id}]
  const [uploading, setUploading] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [aiLoading, setAiLoading] = useState(''); // '' | 'title' | 'description'
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    price: '',
    city: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      setPageLoading(true);
      try {
        const [categoriesRes, statsRes, citiesRes] = await Promise.all([
          categoriesApi.getAll(),
          userApi.getStats(),
          citiesApi.getAll()
        ]);
        setCategories(categoriesRes.data);
        setUserStats(statsRes.data);
        setCities(citiesRes.data);

        // Auto-detect user city
        try {
          const geoRes = await geoApi.detect();
          if (geoRes.data?.suggested_city) {
            setFormData(prev => ({ ...prev, city: geoRes.data.suggested_city }));
          }
        } catch { /* ignore geo errors */ }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load page data');
      } finally {
        setPageLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (value) => {
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleCityChange = (value) => {
    setFormData(prev => ({ ...prev, city: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_IMAGES - images.length;

    if (files.length > remaining) {
      toast.error(`You can add ${remaining} more image${remaining !== 1 ? 's' : ''} (max ${MAX_IMAGES})`);
    }

    const validFiles = files.slice(0, remaining).filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });

    // Use stable unique IDs for each image to prevent React key issues
    const newImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    }));

    setImages(prev => [...prev, ...newImages]);

    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id) => {
    setImages(prev => {
      const removed = prev.find(img => img.id === id);
      if (removed?.preview?.startsWith('blob:')) URL.revokeObjectURL(removed.preview);
      return prev.filter(img => img.id !== id);
    });
  };

  const handleAiGenerate = async (field) => {
    // Use existing title/description as context, or category as hint
    const context = field === 'title' 
      ? (formData.description || formData.category || '')
      : (formData.title || formData.category || '');
    
    if (!context.trim()) {
      toast.error(field === 'title' 
        ? 'Add a description or select a category first' 
        : 'Add a title or select a category first');
      return;
    }

    setAiLoading(field);
    try {
      const { data } = await listingsApi.aiGenerate({
        user_input: field === 'title' 
          ? `Generate only a title for: ${context}` 
          : `Generate only a description for: ${formData.title || context}`,
        category: formData.category || null
      });
      if (field === 'title' && data.title) {
        setFormData(prev => ({ ...prev, title: data.title }));
        toast.success('Title generated!');
      }
      if (field === 'description' && data.description) {
        setFormData(prev => ({ ...prev, description: data.description }));
        toast.success('Description generated!');
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiLoading('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.category || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.city) {
      toast.error('Please select a city');
      return;
    }

    if (images.length === 0) {
      toast.error('Please add at least 1 image');
      return;
    }

    setLoading(true);
    setUploading(true);

    try {
      // Upload all images sequentially with retry
      const uploadedUrls = [];
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        let uploaded = false;
        for (let attempt = 0; attempt < 2 && !uploaded; attempt++) {
          try {
            const res = await uploadApi.uploadImage(img.file);
            uploadedUrls.push(res.data.url);
            uploaded = true;
          } catch (err) {
            if (attempt === 1) {
              toast.error(`Failed to upload image ${i + 1}`);
            }
          }
        }
      }

      if (uploadedUrls.length === 0) {
        toast.error('All image uploads failed. Please try again.');
        setLoading(false);
        setUploading(false);
        return;
      }

      setUploading(false);

      const payload = {
        title: formData.title,
        category: formData.category,
        description: formData.description,
        price: formData.price ? parseFloat(formData.price) : null,
        images: uploadedUrls,
        city: formData.city || null,
        country: "Canada"
      };

      await listingsApi.create(payload);
      toast.success('Listing created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = formatApiError(error);
      toast.error(message);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const canCreateMore = userStats?.can_create_more !== false;
  const listingCount = userStats?.listing_count || 0;
  const listingLimit = userStats?.listing_limit === -1 ? 'Unlimited' : userStats?.listing_limit || 5;

  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="create-listing-loading">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="create-listing-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 
          className="text-3xl font-bold mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Create Listing
        </h1>
        <p className="text-muted-foreground mb-8">
          Post your item, service, job, or housing listing
        </p>

        {/* Listing Limit Warning */}
        {!canCreateMore && (
          <Card className="mb-6 border-[#E53935]/20 bg-[#E53935]/5" data-testid="limit-warning">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-[#E53935] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">You've reached your free limit</p>
                <p className="text-sm text-gray-500 mt-1">
                  You have {listingCount} out of {listingLimit} free listings. Upgrade to Pro for unlimited listings — <strong className="text-primary">FREE until August!</strong>
                </p>
                <Button 
                  variant="default" 
                  size="sm" 
                  className="mt-3 bg-gradient-to-r from-primary to-primary/80"
                  onClick={() => navigate('/dashboard', { state: { tab: 'subscription' } })}
                  data-testid="upgrade-plan-btn"
                >
                  Get Pro Free
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Listing Count */}
        {canCreateMore && userStats && (
          <Card className="mb-6 bg-muted/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                You have <span className="font-medium text-foreground">{listingCount}</span> out of{' '}
                <span className="font-medium text-foreground">{listingLimit}</span> listings
              </p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Title */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="title">Title *</Label>
                  <button
                    type="button"
                    onClick={() => handleAiGenerate('title')}
                    disabled={!!aiLoading || !canCreateMore}
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="ai-generate-title-btn"
                  >
                    {aiLoading === 'title' ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="h-3 w-3" />
                    )}
                    Generate with AI
                  </button>
                </div>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="What are you listing?"
                  disabled={!canCreateMore}
                  data-testid="title-input"
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={handleCategoryChange}
                  disabled={!canCreateMore}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id} data-testid={`category-option-${cat.id}`}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Select
                  value={formData.city}
                  onValueChange={handleCityChange}
                  disabled={!canCreateMore}
                >
                  <SelectTrigger data-testid="city-select">
                    <SelectValue placeholder="Select a city" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city.id} value={city.id} data-testid={`city-option-${city.id}`}>
                        {city.name}, {city.province}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="description">Description *</Label>
                  <button
                    type="button"
                    onClick={() => handleAiGenerate('description')}
                    disabled={!!aiLoading || !canCreateMore}
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="ai-generate-desc-btn"
                  >
                    {aiLoading === 'description' ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="h-3 w-3" />
                    )}
                    Generate with AI
                  </button>
                </div>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your listing in detail..."
                  rows={5}
                  disabled={!canCreateMore}
                  data-testid="description-input"
                />
              </div>

              {/* Price */}
              <div className="space-y-2">
                <Label htmlFor="price">Price (CAD) - Optional</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  disabled={!canCreateMore}
                  data-testid="price-input"
                />
              </div>

              {/* Image Upload - Multi */}
              <div className="space-y-2">
                <Label>Images * <span className="text-muted-foreground font-normal">({images.length}/{MAX_IMAGES})</span></Label>

                {/* Image Previews Grid */}
                {images.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-3" data-testid="image-previews">
                    {images.map((img, idx) => (
                      <div key={img.id} className="relative aspect-square rounded-xl overflow-hidden border border-border group">
                        <img
                          src={img.preview}
                          alt={`Preview ${idx + 1}`}
                          className="w-full h-full object-cover"
                          data-testid={`image-preview-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(img.id)}
                          className="absolute top-1 right-1 p-1 bg-background/90 rounded-full opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
                          data-testid={`remove-image-${idx}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                        {idx === 0 && (
                          <span className="absolute bottom-1 left-1 text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-medium">
                            Cover
                          </span>
                        )}
                      </div>
                    ))}

                    {/* Add More Button */}
                    {images.length < MAX_IMAGES && canCreateMore && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/40 flex flex-col items-center justify-center gap-1 transition-colors"
                        data-testid="add-more-images-btn"
                      >
                        <ImagePlus className="h-5 w-5 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground">Add</span>
                      </button>
                    )}
                  </div>
                )}

                {/* Empty Upload Area */}
                {images.length === 0 && (
                  <div
                    onClick={() => canCreateMore && fileInputRef.current?.click()}
                    className={`image-upload ${!canCreateMore ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="image-upload-area"
                  >
                    <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Click to upload images
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      1-{MAX_IMAGES} images required. Max 5MB each, JPG/PNG/WebP
                    </p>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageChange}
                  className="hidden"
                  multiple
                  disabled={!canCreateMore}
                />
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={loading || !canCreateMore}
                data-testid="submit-listing-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {uploading ? `Uploading images (${images.length})...` : 'Creating...'}
                  </>
                ) : (
                  'Create Listing'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
